<template>
  <!-- Page Principale : Architecture de l'Offre -->
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <!-- Entête de Page -->
    <div class="flex sm:flex-row flex-col justify-between items-start sm:items-center border-b pb-5 gap-4 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
          Catalogue des Prestations
        </h1>
        <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          Structurez votre offre, définissez les temps d'occupation et ajustez votre grille tarifaire.
        </p>
      </div>

      <div class="flex flex-wrap gap-2 sm:w-auto w-full">
        <button @click="router.push('/services/categories')"
          class="px-3 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm flex items-center justify-center gap-2"
          :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-300' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-600'">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
          Catégorie
        </button>
        <button @click="router.push('/services/categories/bulk')"
          class="px-3 py-2 rounded-xl text-[10px] font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm flex items-center justify-center gap-1.5"
          :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400' : 'bg-white hover:bg-zinc-100 border-zinc-200 text-zinc-500'">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
         Catégorie Multiple
        </button>
        <button @click="router.push('/services/new')"
          class="px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-sm cursor-pointer flex items-center justify-center gap-2"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
          Prestation
        </button>
        <button @click="router.push('/services/bulk')"
          class="px-3 py-2 rounded-xl text-[10px] font-semibold uppercase tracking-wider transition-colors cursor-pointer border shadow-sm flex items-center justify-center gap-1.5"
          :class="isDark ? 'bg-zinc-900/60 hover:bg-zinc-800 border-zinc-800 text-zinc-300' : 'bg-zinc-100 hover:bg-zinc-200 border-zinc-200 text-zinc-700'">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
         Prestation Multiple
        </button>
      </div>
    </div>

    <!-- État de chargement -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="text-xs font-medium animate-pulse" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Chargement du catalogue...</div>
    </div>

    <!-- Grille du catalogue -->
    <div v-else class="space-y-8 w-full">
      <div v-for="category in catalogData" :key="category._id" 
        class="border rounded-2xl p-5 space-y-4 transition-colors"
        :class="isDark ? 'bg-zinc-900/10 border-zinc-900' : 'bg-white border-zinc-200 shadow-sm'">
        
        <!-- Entête de catégorie -->
        <div class="flex justify-between items-center border-b pb-3 transition-colors" :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
          <div class="flex items-center gap-3">
            <span class="w-1.5 h-3 rounded-sm bg-amber-500/60"></span>
            <div class="space-y-0.5">
              <h2 class="text-sm font-semibold uppercase tracking-wide" :class="isDark ? 'text-white' : 'text-zinc-900'">{{ category.nom }}</h2>
              <p class="text-[11px] font-medium" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">{{ category.description || 'Aucune description' }}</p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-[10px] font-semibold px-2 py-0.5 rounded transition-colors" :class="isDark ? 'bg-zinc-900 text-zinc-500' : 'bg-zinc-100 text-zinc-500'">
              {{ category.services.length }} prestation(s)
            </span>
            <button @click="editCategory(category)" class="transition-colors p-1 text-xs cursor-pointer" :class="isDark ? 'text-zinc-600 hover:text-white' : 'text-zinc-400 hover:text-zinc-900'">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
            </button>
          </div>
        </div>

        <!-- Tableau des prestations -->
        <div class="overflow-x-auto no-scrollbar">
          <table class="w-full text-xs text-left">
            <thead class="uppercase tracking-wider font-semibold transition-colors" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
              <tr>
                <th class="pb-2.5 font-medium">Libellé de la prestation</th>
                <th class="pb-2.5 font-medium">Temps d'exécution</th>
                <th class="pb-2.5 font-medium">Tarif</th>
                <th class="pb-2.5 font-medium">Canal</th>
                <th class="pb-2.5 text-right font-medium pr-4">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y font-medium transition-colors" :class="isDark ? 'divide-zinc-900/30' : 'divide-zinc-100'">
              <tr v-for="service in category.services" :key="service._id" class="group transition-colors"
                :class="isDark ? 'hover:bg-zinc-900/10' : 'hover:bg-zinc-50'">
                
                <td class="py-3.5 pr-4 whitespace-nowrap">
                  <div class="font-semibold transition-colors" :class="isDark ? 'text-zinc-200 group-hover:text-white' : 'text-zinc-900'">{{ service.nom }}</div>
                  <div class="text-[10px] font-medium mt-0.5" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">{{ service.description || 'Aucune spécification' }}</div>
                </td>

                <td class="py-3.5 pr-4 whitespace-nowrap text-zinc-500">
                  <div class="flex items-center gap-1.5">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>{{ service.duree }} min</span>
                  </div>
                </td>

                <td class="py-3.5 pr-4 whitespace-nowrap font-semibold" :class="isDark ? 'text-amber-500' : 'text-zinc-900'">
                  {{ service.prix }} €
                </td>

                <td class="py-3.5 pr-4 whitespace-nowrap">
                  <span class="px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide border transition-colors" 
                    :class="service.disponible_en_ligne 
                      ? (isDark ? 'bg-emerald-950/20 text-emerald-400 border-emerald-900/30' : 'bg-emerald-50 text-emerald-700 border-emerald-100')
                      : (isDark ? 'bg-zinc-900 text-zinc-600 border-zinc-800' : 'bg-zinc-100 text-zinc-500 border-zinc-200')">
                    {{ service.disponible_en_ligne ? 'Réservable' : 'Salon uniquement' }}
                  </span>
                </td>

                <td class="py-3.5 text-right whitespace-nowrap pr-4">
                  <div class="flex items-center justify-end gap-1.5 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button @click="editService(service)" class="p-1.5 rounded-lg border transition-colors cursor-pointer focus:outline-none" 
                      :class="isDark ? 'bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-400 hover:text-white' : 'bg-white hover:bg-zinc-50 border-zinc-200 text-zinc-500'" 
                      title="Modifier">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                    </button>
                    <button @click="deleteService(service._id)" class="p-1.5 rounded-lg border transition-colors cursor-pointer focus:outline-none" 
                      :class="isDark ? 'bg-zinc-900 hover:bg-rose-950/60 border-zinc-800 text-zinc-600 hover:text-rose-400' : 'bg-white hover:bg-rose-50 border-zinc-200 text-zinc-400 hover:text-rose-600'" 
                      title="Supprimer">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.12-2.212a37.96 37.96 0 00-3.435-.116c-1.173 0-2.31.04-3.435.116-1.21.048-2.12 1.032-2.12 2.212v.916m7.5 0H7.5" /></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- État vide -->
      <div v-if="catalogData.length === 0" class="text-center py-20">
        <p class="text-xs font-medium" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">Aucune catégorie disponible</p>
        <button @click="router.push('/services/categories')" class="mt-4 px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer"
          :class="isDark ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-900 hover:bg-zinc-800 text-white'">
          Créer ma première catégorie
        </button>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
const router = useRouter()
const isLoading = ref(true)
const catalogData = ref([])

onMounted(async () => {
  await fetchCatalog()
})

const fetchCatalog = async () => {
  try {
    isLoading.value = true

    const [categoriesResponse, servicesResponse] = await Promise.all([
      api.services.getAllCategories(),
      api.services.getAllServices()
    ])

    // Backend retourne des formats différents:
    // - Catégories: { success: true, message: '...', categories: [...] }
    // - Services: directement le tableau [...]
    const categories = categoriesResponse.data?.categories || categoriesResponse.data || []
    const services = Array.isArray(servicesResponse.data) ? servicesResponse.data : []

    console.log('Categories:', categories)
    console.log('Services:', services)

    if (!Array.isArray(categories)) {
      console.error('Categories n\'est pas un tableau:', categories)
      toast.error('Format de réponse incorrect pour les catégories')
      return
    }

    catalogData.value = categories.map(cat => ({
      ...cat,
      services: services.filter(srv => srv.categorie?._id === cat._id || srv.categorie === cat._id)
    }))

  } catch (error) {
    console.error('Erreur lors du chargement du catalogue:', error)
    toast.error('Erreur lors du chargement du catalogue', {
      position: 'top-right',
      autoClose: 3000
    })
  } finally {
    isLoading.value = false
  }
}

const editCategory = (cat) => {
  router.push(`/services/categories?edit=${cat._id}`)
}

const editService = (srv) => {
  router.push(`/services/new?edit=${srv._id}`)
}

const deleteService = async (id) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
    return
  }

  try {
    await api.services.deleteService(id)
    toast.success('Service supprimé avec succès', {
      position: 'top-right',
      autoClose: 2000
    })
    await fetchCatalog()
  } catch (error) {
    console.error('Erreur lors de la suppression:', error)
    toast.error('Erreur lors de la suppression du service', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
